import { AsyncPipe } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatOptionModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { map, merge, Observable, startWith, Subject } from "rxjs";
import { DefaultComponent } from "src/app/default.component";
import { IconReplacePipe } from "src/pipes/icon-replace.pipe";
import { PrefixTemplate } from "../../PrefixTemplate";
import { PrefixValidator } from "../../PrefixValidator";
import { TemplateHTMLComponent } from "../template-html/template-html.component";
import { SelectGroup, SelectOption } from "../template-select/template-select.component";

@Component({
  selector: "app-template-search",
  imports: [
    MatSelectModule,
    MatOptionModule,
    TemplateHTMLComponent,
    ReactiveFormsModule,
    IconReplacePipe,
    MatAutocompleteModule,
    AsyncPipe,
    MatIconModule,
  ],
  templateUrl: "./template-search.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./template-search.component.less",
})
export class TemplateSearchComponent extends DefaultComponent implements PrefixTemplate<string>, OnInit {
  @Input({ required: true })
  public control: FormControl<string | null> | null;

  @Input()
  public value: string | null;

  @Input()
  public label: string | null;

  @Input()
  public required: boolean;

  @Input()
  public disabled: boolean;

  @Input()
  public options: SelectOption[];

  @Input()
  public groups: SelectGroup[];

  @Input()
  public multiple: boolean;

  @Input()
  public selectFirstIfEmpty: boolean;

  @ViewChild("input")
  public input: ElementRef<HTMLInputElement> | null;

  @Output()
  public onblur: EventEmitter<void>;

  public filteredOptions?: Observable<SelectOption[]>;
  public filteredGroups?: Observable<SelectGroup[]>;
  public textChange: Subject<void>;

  public constructor() {
    super();
    this.control = null;
    this.value = null;
    this.label = null;
    this.required = false;
    this.disabled = false;
    this.selectFirstIfEmpty = true;

    this.options = [];
    this.groups = [];
    this.multiple = false;
    this.textChange = new Subject();
    this.onblur = new EventEmitter();
    this.input = null;
  }

  public ngOnInit(): void {
    const control = this.control;
    if (control) {
      this.addValidators(control);
      this.selectFirstIfEmpty && !this.multiple && !control.value ? this.selectFirst(control) : control.markAsTouched();

      this.filteredOptions = merge(this.textChange, control.valueChanges).pipe(
        startWith(""),
        map(() => this.filterOptions(this.input?.nativeElement.value, control.value)),
      );
      this.filteredGroups = merge(this.textChange, control.valueChanges).pipe(
        startWith(""),
        map(() => this.filterGroups(this.input?.nativeElement.value, control.value)),
      );
    } else {
      throw new Error("Undefined control");
    }
  }

  public displayLabel(): (value: string | null) => string {
    const options = this.options.slice();
    const groups = this.groups.slice();
    return (value: string | null): string => {
      let label = "";
      for (const option of options) {
        if (option.value === value) label = option.label;
      }
      for (const group of groups) {
        for (const option of group.options) {
          if (option.value === value) label = option.label;
        }
      }
      return label || value || "";
    };
  }

  public getOption(value: string | string[] | null): SelectOption | null {
    return this.options.find((option) => option.value == value) ?? null;
  }

  private addValidators(control: FormControl<string | string[] | null>): void {
    if (this.required) control.addValidators([PrefixValidator.required()]);

    control.updateValueAndValidity();
  }

  private filterOptions(
    inputValue: string | null | undefined,
    controlValue: string | null | undefined,
    options: SelectOption[] = this.options,
  ): SelectOption[] {
    if (inputValue && controlValue) {
      const option = this.getOption(controlValue);
      if (option && option.label === inputValue) {
        return options;
      }
    }
    if (!inputValue) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes((inputValue ?? "").toLowerCase()));
  }

  private filterGroups(
    inputValue: string | null | undefined,
    controlValue: string | null | undefined,
    groups: SelectGroup[] = this.groups,
  ): SelectGroup[] {
    return groups
      .map((group) => {
        return { ...group, options: this.filterOptions(inputValue, controlValue, group.options) };
      })
      .filter((group) => group.options.length);
  }

  private selectFirst(control: FormControl): void {
    console.log("selecting first");
    if (this.options.length) control.setValue(this.value || this.options[0].value);
    if (this.groups.length) control.setValue(this.value || this.options[0].value);
  }
}
